/**
 * Voxly — CommentsSheet.
 *
 * Facebook-style bottom-sheet that opens when the user taps the comments
 * button on a post. Renders all voice comments (and one level of replies)
 * in a tall scrollable panel with a sticky "Record voice comment" bar at
 * the bottom.
 *
 * Voxly is strictly voice-only for comments, so the footer triggers the
 * VoiceRecorder modal rather than exposing a text input.
 *
 * Props:
 *   open              boolean
 *   onClose           () => void
 *   postAuthorName    string — used in the title ("Comments on Sara's post")
 *   comments          [] | null
 *   loading           boolean
 *   onRecord          (parentCommentId?: string) => void   — opens recorder
 *   onReport          (commentId: string) => void
 *   renderMention     (transcript, mentions) => ReactNode  — shared from PostCard
 */
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { X, CaretDown, Microphone, Flag, ChatTeardrop } from "@phosphor-icons/react";
import WaveformPlayer from "@/components/WaveformPlayer";
import { fileUrl, avatarUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

const SORTS = [
  { id: "newest", label: "Most recent" },
  { id: "oldest", label: "Oldest first" },
];

export default function CommentsSheet({
  open,
  onClose,
  postAuthorName,
  comments,
  loading,
  onRecord,
  onReport,
  renderMention,
}) {
  const { user: me } = useAuth();
  const [sort, setSort] = useState("newest");
  const [sortOpen, setSortOpen] = useState(false);

  // Lock body scroll while the sheet is open so the page beneath doesn't
  // scroll along with the comment list.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Roots + replies tree, then apply the chosen sort to roots only. Reply
  // ordering is always chronological (oldest-first) per Facebook convention.
  const tree = useMemo(() => {
    if (!comments) return null;
    const replyMap = new Map();
    const roots = [];
    for (const c of comments) {
      if (c.parent_comment_id) {
        const arr = replyMap.get(c.parent_comment_id) || [];
        arr.push(c);
        replyMap.set(c.parent_comment_id, arr);
      } else {
        roots.push(c);
      }
    }
    roots.sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return sort === "newest" ? tb - ta : ta - tb;
    });
    for (const [k, arr] of replyMap.entries()) {
      arr.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      replyMap.set(k, arr);
    }
    return { roots, replyMap };
  }, [comments, sort]);

  if (!open) return null;

  const renderComment = (c, isReply) => (
    <div
      key={c.comment_id}
      className={`flex items-start gap-3 group ${isReply ? "ml-10 mt-2" : "mt-3"}`}
      data-testid={`comments-sheet-comment-${c.comment_id}`}
    >
      <Link to={`/u/${c.user_id}`} onClick={onClose} className="shrink-0">
        <img
          src={avatarUrl(c.user_picture)}
          alt=""
          className={`${isReply ? "h-7 w-7" : "h-9 w-9"} rounded-full object-cover border border-border`}
        />
      </Link>
      <div className="flex-1 min-w-0">
        {/* FB-style "bubble" — author + voice player share a rounded container. */}
        <div className="inline-block max-w-full bg-secondary/55 border border-border/40 rounded-2xl px-3 py-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link
              to={`/u/${c.user_id}`}
              onClick={onClose}
              className="text-sm font-bold hover:underline truncate"
            >
              {c.user_name}
            </Link>
            {isReply && c.parent_user_id && c.parent_user_name && (
              <Link
                to={`/u/${c.parent_user_id}`}
                onClick={onClose}
                data-testid={`comments-sheet-mention-${c.comment_id}`}
                className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-mono hover:bg-primary/25 transition"
              >
                @{c.parent_user_name}
              </Link>
            )}
            {c.moderation_action === "review" && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-400 font-mono uppercase">
                flagged
              </span>
            )}
          </div>
          <div className="mt-1.5">
            <WaveformPlayer
              src={fileUrl(c.audio_path)}
              duration={c.duration}
              compact
              testid={`comments-sheet-player-${c.comment_id}`}
            />
          </div>
          {c.transcript && (
            <p
              className="text-[12px] text-muted-foreground italic mt-1.5 line-clamp-3"
              data-testid={`comments-sheet-transcript-${c.comment_id}`}
            >
              &ldquo;{renderMention ? renderMention(c.transcript, c.mentions || []) : c.transcript}&rdquo;
            </p>
          )}
        </div>
        {/* Action row — timestamp + Reply + Report (FB style: subtle under-bubble buttons). */}
        <div className="flex items-center gap-3 mt-1 px-1.5 text-[11px] text-muted-foreground">
          <span className="font-mono">{timeAgo(c.created_at)} ago</span>
          {!isReply && (
            <button
              onClick={() => onRecord(c.comment_id)}
              data-testid={`comments-sheet-reply-${c.comment_id}`}
              className="font-bold hover:text-primary transition"
            >
              Reply
            </button>
          )}
          {!isReply && c.reply_count > 0 && <span>· {c.reply_count}</span>}
          <button
            onClick={() => onReport(c.comment_id)}
            data-testid={`comments-sheet-report-${c.comment_id}`}
            className="ml-auto opacity-0 group-hover:opacity-100 hover:text-destructive transition"
            aria-label="Report"
          >
            <Flag size={12} />
          </button>
        </div>
      </div>
    </div>
  );

  const totalRoots = tree?.roots.length || 0;
  const totalReplies = comments ? comments.length - totalRoots : 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end justify-center"
      onClick={onClose}
      data-testid="comments-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-card rounded-t-3xl border-t border-border flex flex-col h-[88vh] sm:h-[80vh] animate-in slide-in-from-bottom duration-200"
      >
        {/* Drag-handle bar — visual only on this build, but mirrors FB sheet UX. */}
        <div className="pt-2 pb-1 flex justify-center">
          <div className="h-1.5 w-12 rounded-full bg-border" />
        </div>

        {/* Header */}
        <div className="px-4 pb-3 flex items-center justify-between border-b border-border/60">
          <div className="min-w-0">
            <h2 className="font-heading text-lg font-bold leading-tight" data-testid="comments-sheet-title">
              Comments
            </h2>
            {totalRoots > 0 && (
              <p className="text-[11px] text-muted-foreground font-mono uppercase tracking-widest">
                {totalRoots} {totalRoots === 1 ? "voice reply" : "voice replies"}
                {totalReplies > 0 ? ` · ${totalReplies} sub-${totalReplies === 1 ? "reply" : "replies"}` : ""}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close comments"
            data-testid="comments-sheet-close"
            className="p-1.5 -mr-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition"
          >
            <X size={22} />
          </button>
        </div>

        {/* Sort selector */}
        <div className="px-4 pt-2 pb-1 relative">
          <button
            onClick={() => setSortOpen((v) => !v)}
            data-testid="comments-sheet-sort"
            className="inline-flex items-center gap-1 text-sm font-bold text-foreground hover:text-primary transition"
          >
            {SORTS.find((s) => s.id === sort)?.label}
            <CaretDown size={14} weight="bold" />
          </button>
          {sortOpen && (
            <div
              className="absolute left-4 mt-1 z-10 bg-card border border-border rounded-xl py-1 min-w-[160px] shadow-lg"
              data-testid="comments-sheet-sort-menu"
            >
              {SORTS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => { setSort(s.id); setSortOpen(false); }}
                  data-testid={`comments-sort-${s.id}`}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-secondary/50 transition ${sort === s.id ? "text-primary font-bold" : ""}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Comment list */}
        <div
          className="flex-1 overflow-y-auto px-4 pb-3 pt-1"
          data-testid="comments-sheet-list"
        >
          {loading && (
            <div className="py-8 flex justify-center" data-testid="comments-sheet-loading">
              <div className="h-7 w-7 rounded-full border-2 border-primary/60 border-t-transparent animate-spin" />
            </div>
          )}
          {!loading && tree && tree.roots.length === 0 && (
            <div className="py-16 text-center" data-testid="comments-sheet-empty">
              <div className="mx-auto h-14 w-14 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mb-3">
                <ChatTeardrop size={26} className="text-primary" weight="duotone" />
              </div>
              <p className="font-heading text-base font-bold">No voice replies yet.</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Tap the mic below to leave the first one.
              </p>
            </div>
          )}
          {!loading && tree && tree.roots.map((c) => (
            <div key={`thread-${c.comment_id}`}>
              {renderComment(c, false)}
              {(tree.replyMap.get(c.comment_id) || []).map((r) => renderComment(r, true))}
            </div>
          ))}
        </div>

        {/* Sticky bottom — voice-only "Comment as <me>" trigger. */}
        <div className="border-t border-border/60 px-4 py-3 bg-card">
          <button
            onClick={() => onRecord(null)}
            data-testid="comments-sheet-record"
            className="w-full flex items-center gap-3 p-3 rounded-full bg-secondary/60 hover:bg-secondary border border-border hover:border-primary/50 transition active:scale-[0.99]"
          >
            <img src={avatarUrl(me?.picture)} alt="" className="h-8 w-8 rounded-full object-cover border border-border" />
            <span className="flex-1 text-left text-sm text-muted-foreground truncate">
              Comment as <span className="font-semibold text-foreground">{me?.name?.split(" ")[0] || "you"}</span>
            </span>
            <span className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md">
              <Microphone size={18} weight="fill" />
            </span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
